import React, { useState, useEffect } from 'react';
import { getProviders, getEntries, getRequests } from '../services/api';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Cake, Info, Star, Flag } from 'lucide-react';
import { getHolidayInfo } from '../utils/holidays';

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

const DAY_NAMES = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

const CalendarPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [providers, setProviders] = useState([]);
  const [entries, setEntries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [providersData, entriesData, requestsData] = await Promise.all([
        getProviders(),
        getEntries(true),  // forCalendar=true to get all entries
        getRequests(true)  // forCalendar=true to get all pending requests
      ]);
      setProviders(providersData);
      setEntries(entriesData);
      setRequests(requestsData);
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeMonth = (offset) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + offset);
    setCurrentDate(newDate);
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];

    // Empty cells for days before the first day of month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="calendar-day bg-slate-50"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const currentDateObj = new Date(dateStr);

      const dayEvents = [];
      
      // Check holidays
      const holidayInfo = getHolidayInfo(year, month + 1, day);

      // Check birthdays
      providers.forEach(p => {
        if (p.birthday) {
          const bDate = new Date(p.birthday + 'T12:00:00');
          if (bDate.getMonth() === month && bDate.getDate() === day) {
            dayEvents.push({
              type: 'birthday',
              provider: p,
              name: p.name
            });
          }
        }
      });

      // Check confirmed entries
      entries.forEach(e => {
        // Parse dates as simple strings to avoid timezone issues
        const [startYear, startMonth, startDay] = e.start_date.split('-').map(Number);
        const [endYear, endMonth, endDay] = e.end_date.split('-').map(Number);
        
        const startDate = new Date(startYear, startMonth - 1, startDay);
        const endDate = new Date(endYear, endMonth - 1, endDay);
        const checkDate = new Date(year, month, day);

        if (checkDate >= startDate && checkDate <= endDate) {
          const provider = providers.find(p => p.id === e.provider_id);
          dayEvents.push({
            type: 'entry',
            provider,
            name: provider?.name || '?'
          });
        }
      });

      // Check pending requests
      requests.forEach(r => {
        // Parse dates as simple strings to avoid timezone issues
        const [startYear, startMonth, startDay] = r.start_date.split('-').map(Number);
        const [endYear, endMonth, endDay] = r.end_date.split('-').map(Number);
        
        const startDate = new Date(startYear, startMonth - 1, startDay);
        const endDate = new Date(endYear, endMonth - 1, endDay);
        const checkDate = new Date(year, month, day);

        if (checkDate >= startDate && checkDate <= endDate) {
          const provider = providers.find(p => p.id === r.provider_id);
          dayEvents.push({
            type: 'request',
            provider,
            name: provider?.name || '?'
          });
        }
      });

      // Determine if it's a holiday for background color
      const isHoliday = holidayInfo.isBrazilianHoliday || holidayInfo.isJewishHoliday;
      const bgClass = isHoliday ? 'bg-amber-50/50' : '';

      days.push(
        <div 
          key={day} 
          className={`calendar-day border-t border-l border-slate-100 relative hover:bg-slate-50 transition ${bgClass}`}
          data-testid={`calendar-day-${day}`}
        >
          <div className="text-right text-slate-400 text-xs font-semibold mb-1">{day}</div>
          
          {/* Brazilian Holiday */}
          {holidayInfo.isBrazilianHoliday && (
            <div
              className="event-bar flex items-center gap-1 bg-green-100 text-green-700 border-l-2 border-green-500"
              title={`Feriado Nacional: ${holidayInfo.brazilianName}`}
            >
              <Flag className="h-3 w-3" />
              <span className="truncate">{holidayInfo.brazilianName}</span>
            </div>
          )}
          
          {/* Jewish Holiday */}
          {holidayInfo.isJewishHoliday && (
            <div
              className="event-bar flex items-center gap-1 bg-sky-100 text-sky-700 border-l-2 border-sky-500"
              title={`Feriado Judaico: ${holidayInfo.jewishName}`}
            >
              <Star className="h-3 w-3" />
              <span className="truncate">{holidayInfo.jewishName}</span>
            </div>
          )}
          
          {dayEvents.map((event, idx) => (
            <div
              key={idx}
              className={`event-bar flex items-center gap-1 ${
                event.type === 'birthday' 
                  ? 'bg-pink-100 text-pink-700 border-l-2 border-pink-500' 
                  : event.type === 'entry'
                  ? 'bg-indigo-100 text-indigo-700 border-l-2 border-indigo-500'
                  : 'bg-purple-100 text-purple-700 border-l-2 border-purple-400 opacity-75'
              }`}
              title={
                event.type === 'birthday' 
                  ? `Flexibilidade: Data especial de ${event.name}`
                  : event.type === 'entry'
                  ? `Indisponibilidade confirmada: ${event.name}`
                  : `Sinalização pendente: ${event.name}`
              }
            >
              {event.type === 'birthday' && <Cake className="h-3 w-3" />}
              <span className="truncate">{event.name}{event.type === 'request' ? ' (?)' : ''}</span>
            </div>
          ))}
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      <header className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-slate-900">Cronograma de Disponibilidade</h2>
          <p className="text-slate-500 text-sm">Visualize períodos de indisponibilidade, feriados e datas especiais.</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-indigo-500 rounded-sm"></div> Confirmado
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-purple-400 rounded-sm"></div> Sinalizado
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-500 rounded-sm"></div> Feriado BR
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-sky-500 rounded-sm"></div> Feriado Judaico
            </span>
            <span className="flex items-center gap-1">
              <Cake className="h-4 w-4 text-pink-500" /> Flexibilidade
            </span>
          </div>
          <div className="flex bg-white rounded-lg shadow-sm border border-slate-200">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => changeMonth(-1)}
              className="border-r border-slate-200"
              data-testid="calendar-prev"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <span className="px-4 py-2 font-semibold min-w-[150px] text-center" data-testid="calendar-month-label">
              {MONTH_NAMES[currentDate.getMonth()]} {currentDate.getFullYear()}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => changeMonth(1)}
              className="border-l border-slate-200"
              data-testid="calendar-next"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      <Card className="border-slate-200 overflow-hidden" data-testid="calendar-view">
        {/* Days Header */}
        <div className="calendar-grid border-b border-slate-200">
          {DAY_NAMES.map(day => (
            <div key={day} className="calendar-header">{day}</div>
          ))}
        </div>
        {/* Calendar Grid */}
        <div className="calendar-grid">
          {renderCalendar()}
        </div>
      </Card>

      <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-lg text-sm text-indigo-800 flex items-center gap-2">
        <Info className="h-4 w-4" />
        <span>Nota: O cronograma exibe o período completo, mas a contagem obedece à regra híbrida (dias úteis para períodos curtos, corridos para longos).</span>
      </div>
    </div>
  );
};

export default CalendarPage;
